import type { AppColorMode } from './contentColors';

/**
 * Operational status tones. Colour is used sparingly and always paired with a
 * text label (and, for `active`, a dot) — never as the only signal.
 *
 * - active      in progress right now (purple, with a dot)
 * - complete    done (ink — positive without green)
 * - pending     waiting / scheduled (amber)
 * - attention   needs a look: alarms, delays (orange)
 * - failed      cancelled, rejected, fire alarm (red)
 * - offline     out of service / not reporting (grey)
 * - unassigned  no truck / driver / owner (outlined neutral)
 */
export type StatusTone =
  | 'active'
  | 'complete'
  | 'pending'
  | 'attention'
  | 'failed'
  | 'offline'
  | 'unassigned';

export interface StatusToneTokens {
  /** Dot, marker and bar fill. */
  fill: string;
  /** Label colour (AA on paper). */
  text: string;
  /** Chip / row tint. */
  soft: string;
  border: string;
}

export const STATUS_TONES: Record<AppColorMode, Record<StatusTone, StatusToneTokens>> = {
  light: {
    active: { fill: '#7C3AED', text: '#6D28D9', soft: 'rgba(124, 58, 237, 0.10)', border: 'rgba(124, 58, 237, 0.32)' },
    complete: { fill: '#0F172A', text: '#0F172A', soft: 'rgba(15, 23, 42, 0.06)', border: 'rgba(15, 23, 42, 0.24)' },
    pending: { fill: '#F59E0B', text: '#B45309', soft: 'rgba(245, 158, 11, 0.12)', border: 'rgba(245, 158, 11, 0.40)' },
    attention: { fill: '#EA580C', text: '#C2410C', soft: 'rgba(234, 88, 12, 0.10)', border: 'rgba(234, 88, 12, 0.36)' },
    failed: { fill: '#DC2626', text: '#B91C1C', soft: 'rgba(220, 38, 38, 0.08)', border: 'rgba(220, 38, 38, 0.32)' },
    offline: { fill: '#94A3B8', text: '#64748B', soft: 'rgba(100, 116, 139, 0.10)', border: 'rgba(100, 116, 139, 0.30)' },
    unassigned: { fill: 'transparent', text: '#475569', soft: 'transparent', border: '#94A3B8' },
  },
  dark: {
    active: { fill: '#A78BFA', text: '#C4B5FD', soft: 'rgba(167, 139, 250, 0.16)', border: 'rgba(167, 139, 250, 0.40)' },
    complete: { fill: '#E2E8F0', text: '#E2E8F0', soft: 'rgba(226, 232, 240, 0.08)', border: 'rgba(226, 232, 240, 0.28)' },
    pending: { fill: '#FBBF24', text: '#FCD34D', soft: 'rgba(251, 191, 36, 0.14)', border: 'rgba(251, 191, 36, 0.40)' },
    attention: { fill: '#FB923C', text: '#FDBA74', soft: 'rgba(251, 146, 60, 0.14)', border: 'rgba(251, 146, 60, 0.40)' },
    failed: { fill: '#F87171', text: '#FCA5A5', soft: 'rgba(248, 113, 113, 0.14)', border: 'rgba(248, 113, 113, 0.40)' },
    offline: { fill: '#64748B', text: '#94A3B8', soft: 'rgba(148, 163, 184, 0.12)', border: 'rgba(148, 163, 184, 0.30)' },
    unassigned: { fill: 'transparent', text: '#CBD5E1', soft: 'transparent', border: '#64748B' },
  },
};

export function statusToneTokens(mode: AppColorMode, tone: StatusTone): StatusToneTokens {
  return STATUS_TONES[mode][tone];
}
